package com.evoapps.evoshift.di

import androidx.compose.runtime.Composable
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewmodel.compose.viewModel
import org.kodein.di.compose.localDI
import org.kodein.di.direct
import org.kodein.di.instance

/**
 * A view model built by Kodein but kept in the current ViewModelStore (the navigation entry's):
 * created once per screen, kept while switching tabs (saveState/restoreState keep the store) and
 * cleared, with its Firestore listeners, when the screen leaves the back stack.
 *
 * Use this instead of `rememberInstance()` / `by di.instance()` for `provider` and `factory` view
 * models: those built a new view model on every visit, or even on every recomposition, so screens
 * loaded twice and old listeners never stopped. Singletons (DashboardViewModel,
 * NotificationsViewModel) are app-wide on purpose and stay on rememberInstance().
 */
@Composable
inline fun <reified VM : ViewModel> diViewModel(): VM {
    val di = localDI()
    return viewModel { di.direct.instance<VM>() }
}

/** [diViewModel] for factory bindings: one view model per [arg] (e.g. per roster). */
@Composable
inline fun <reified A : Any, reified VM : ViewModel> diViewModel(arg: A): VM {
    val di = localDI()
    return viewModel(key = "${VM::class.simpleName}:$arg") { di.direct.instance<A, VM>(arg = arg) }
}
